import React, { useState, useRef, useEffect } from 'react';
import { useAmadeusApi } from '@/hooks/useAmadeusApi';

// Tipos simplificados para autocomplete e ofertas
interface HotelAutocomplete {
  name: string;
  hotelId: string;
  address?: { cityName?: string; countryCode?: string };
}

const HotelSearchAmadeus: React.FC = () => {
  const {
    searchHotelsByName,
    searchHotels,
    getHotelOffers,
    getHotelOffersBatch,
  } = useAmadeusApi();

  // Estados do formulário
  const [searchTerm, setSearchTerm] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState<HotelAutocomplete[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<HotelAutocomplete | null>(null);
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref para detectar clique fora do autocomplete
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node)
      ) {
        setAutocompleteResults([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Buscar autocomplete de hotéis
  const fetchHotelAutocomplete = async (term: string) => {
    setAutocompleteResults([]);
    setSelectedHotel(null);
    if (term.length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const data = await searchHotelsByName(term);
      setAutocompleteResults(data);
    } catch (e: any) {
      setError('Erro ao buscar autocomplete.');
    } finally {
      setLoading(false);
    }
  };

  // Buscar hotéis por cidade
  const fetchHotelsByCity = async () => {
    setLoading(true);
    setError(null);
    setResults([]);
    try {
      // Tenta buscar por código IATA (3 letras) ou nome de cidade
      let cityCode = searchTerm.trim().toUpperCase();
      if (cityCode.length !== 3) {
        // Se não for IATA, tenta buscar autocomplete e pegar o iataCode
        const suggestions = await searchHotelsByName(cityCode);
        cityCode = suggestions[0]?.iataCode || cityCode;
      }
      const hotels = await searchHotels(cityCode);
      // Pega até 20 hotelIds para buscar ofertas
      const hotelIds = hotels.map((h: any) => h.hotelId).filter(Boolean).slice(0, 20);
      let offers: any[] = [];
      if (hotelIds.length > 0) {
        offers = await getHotelOffersBatch(
          hotelIds,
          checkInDate,
          checkOutDate,
          adults,
          children
        );
      }
      // Relaciona cada hotel à sua oferta (pelo hotelId)
      const resultsWithPrices = hotels.map((hotel: any) => {
        const offer = offers.find((o: any) => o.hotel?.hotelId === hotel.hotelId);
        let minPrice = Infinity;
        if (offer && offer.offers && offer.offers.length > 0) {
          // Pega o menor preço entre as ofertas
          minPrice = offer.offers.reduce((min: number, curr: any) => {
            const price = parseFloat(curr.price?.total || curr.price?.base || '0');
            return price < min ? price : min;
          }, Infinity);
        }
        return {
          ...hotel,
          minPrice: minPrice !== Infinity ? minPrice.toFixed(2) : '',
          currency: offer?.offers?.[0]?.price?.currency || '',
        };
      });
      setResults(resultsWithPrices);
    } catch (e: any) {
      setError('Erro ao buscar hotéis por cidade.');
    } finally {
      setLoading(false);
    }
  };

  // Buscar ofertas de hotel por hotelId
  const fetchHotelOffers = async (hotelId: string) => {
    setLoading(true);
    setError(null);
    setResults([]);
    try {
      const data = await getHotelOffers(hotelId);
      // Normaliza para array para exibir na tabela
      setResults(Array.isArray(data) ? data : [data]);
    } catch (e: any) {
      setError('Erro ao buscar ofertas do hotel.');
    } finally {
      setLoading(false);
    }
  };

  // Handler do input de busca
  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    fetchHotelAutocomplete(e.target.value);
  };

  // Handler ao clicar em sugestão de hotel
  const handleSelectHotel = (hotel: HotelAutocomplete) => {
    setSelectedHotel(hotel);
    setSearchTerm(hotel.name);
    setAutocompleteResults([]);
  };

  // Handler do botão buscar (busca por cidade)
  const handleCitySearch = () => {
    setSelectedHotel(null);
    fetchHotelsByCity();
  };

  // Handler do botão buscar ofertas do hotel selecionado
  const handleHotelOffersSearch = () => {
    if (selectedHotel && selectedHotel.hotelId) {
      fetchHotelOffers(selectedHotel.hotelId);
    }
  };

  // Handler do submit do formulário
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAutocompleteResults([]); // Fecha o autocomplete ao buscar
    if (selectedHotel) {
      handleHotelOffersSearch();
    } else {
      handleCitySearch();
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
      <h2>Busca de Hotéis (Amadeus)</h2>
      <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
        <div ref={autocompleteRef} style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchTermChange}
            placeholder="Digite o nome do hotel ou cidade"
            style={{ width: '100%', padding: 8, fontSize: 16 }}
            autoComplete="off"
          />
          {autocompleteResults.length > 0 && (
            <ul style={{ position: 'absolute', left: 0, right: 0, background: '#fff', border: '1px solid #ccc', zIndex: 10, listStyle: 'none', margin: 0, padding: 0 }}>
              {autocompleteResults.map((hotel, idx) => (
                <li
                  key={hotel.hotelId + idx}
                  style={{ padding: 8, cursor: 'pointer' }}
                  onClick={() => handleSelectHotel(hotel)}
                >
                  {hotel.name} {hotel.address?.cityName ? `(${hotel.address.cityName})` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div>
            <label>Check-in: <input type="date" value={checkInDate} onChange={e => setCheckInDate(e.target.value)} /></label>
          </div>
          <div>
            <label>Check-out: <input type="date" value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)} /></label>
          </div>
          <div>
            <label>Adultos: <input type="number" min={1} max={9} value={adults} onChange={e => setAdults(Number(e.target.value))} /></label>
          </div>
          <div>
            <label>Crianças: <input type="number" min={0} max={9} value={children} onChange={e => setChildren(Number(e.target.value))} /></label>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button type="submit" style={{ padding: '8px 24px', fontSize: 16 }}>Buscar</button>
        </div>
      </form>
      {loading && <div>Carregando...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <div>
        <h3>Resultados</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Nome</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Cidade</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Preço Mínimo</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Moeda</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{row.name}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{row.address?.cityName || ''}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{row.minPrice || '-'}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{row.currency || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Comentários:
        - Agora usando a API real da Amadeus
        - Adicionar mais filtros conforme necessário
        - Evoluir a tabela para exibir os dados mais relevantes
      */}
    </div>
  );
};

export default HotelSearchAmadeus; 